/* istanbul ignore file */

import { useMemo } from 'react';
import { useSelector } from '@xstate5/react';
import { IngredientKind } from '@sg/graphql-schema';

import {
  checkIfCanMixDressings,
  checkIfDressingIngredient,
  checkIfReachedMaxModifications,
  type CustomizationMachineActorRef,
  type CustomizationMachineTypes,
  generateCombinedDressingDetails,
  getActiveDressingsPortionsNumbers,
  getActiveModificationsCount,
  getMaxModificationLimits,
  getModificationsMap,
  getOutOfStockModifications,
  sortModifications,
} from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Customization context and derived data extracted from the provided machine actor.
 */
export const useCustomizationContext = (params: Params) => {
  const { actor } = params;

  // ─── Flags ───────────────────────────────────────────────────────────────────

  const isFetching = useSelector(actor, (snapshot) => snapshot.matches('fetching')); // prettier-ignore
  const hasFailedToFetch = useSelector(actor, (snapshot) => snapshot.matches('failed-to-fetch')); // prettier-ignore
  const isCustomizationActive = useSelector(actor, (snapshot) => snapshot.matches({ ready: { mode: 'customization' } })); // prettier-ignore
  const isDetailsActive = useSelector(actor, (snapshot) => snapshot.matches({ ready: { mode: 'details' } })); // prettier-ignore
  const isReviewActive = useSelector(actor, (snapshot) => snapshot.matches({ ready: { mode: 'review' } })); // prettier-ignore

  const shouldShowTooFewBasesModal = useSelector(actor, (snapshot) => snapshot.matches({ ready: { mode: { customization: 'confirm-too-few-bases' } } })); // prettier-ignore
  const shouldShowNoDressingModal = useSelector(actor, (snapshot) => snapshot.matches({ ready: { mode: { customization: 'confirm-no-dressing' } } })); // prettier-ignore

  const hasMadeModifications = useSelector(actor, (snapshot) => {
    const { modifications } = snapshot.context;
    const { additions = [], removals = [], substitutions = [] } = modifications ?? {}; // prettier-ignore

    return additions.length + removals.length + substitutions.length > 0;
  });

  // ─── Context ─────────────────────────────────────────────────────────────────

  const lineItemId = useSelector(actor, ({ context }) => context.lineItemId);
  const allModificationsMap = useSelector(actor, ({ context }) => context.allModificationsMap); // prettier-ignore
  const product = useSelector(actor, ({ context }) => context.product);
  const restaurant = useSelector(actor, ({ context }) => context.productRestaurant); // prettier-ignore
  const restaurantSlug = useSelector(actor, ({ context }) => context.restaurantSlug); // prettier-ignore
  const isDelivery = useSelector(actor, ({ context }) => context.isDelivery);
  const netPrice = useSelector(actor, ({ context }) => context.netPrice);
  const calories = useSelector(actor, ({ context }) => context.calories);
  const modifications = useSelector(actor, ({ context }) => context.modifications); // prettier-ignore
  const modificationGroups = useSelector(actor, ({ context }) => context.modificationGroups); // prettier-ignore
  const modificationLimits = useSelector(actor, ({ context }) => context.modificationLimits); // prettier-ignore
  const maxModificationsCount = useSelector(actor, ({ context }) => context.product?.maxModifications ?? Number.POSITIVE_INFINITY); // prettier-ignore
  const additions = useSelector(actor, ({ context }) => context.modifications?.additions ?? []); // prettier-ignore
  const removals = useSelector(actor, ({ context }) => context.modifications?.removals ?? []); // prettier-ignore
  const substitutions = useSelector(actor, ({ context }) => context.modifications?.substitutions ?? []); // prettier-ignore
  const initialModificationsUnsorted = useSelector(actor, ({ context }) => context.modifications?.initial ?? []); // prettier-ignore
  const activeModificationsUnsorted = useSelector(actor, ({ context }) => context.modifications?.active ?? []); // prettier-ignore
  const mixedDressingDetails = useSelector(actor, ({ context }) => context.mixedDressingDetails); // prettier-ignore
  const quantity = useSelector(actor, ({ context }) => context.quantity);

  // ─── Derived Data ────────────────────────────────────────────────────────────

  const productCost = product?.cost ?? 0;
  const totalCost = netPrice + productCost;

  const initialModificationsMap = useMemo<ModificationsMap>(
    () => getModificationsMap(initialModificationsUnsorted),
    [initialModificationsUnsorted],
  );

  const activeModifications = useMemo(
    () => sortModifications(activeModificationsUnsorted),
    [activeModificationsUnsorted],
  );

  const activeModificationsMap = useMemo<ModificationsMap>(
    () => getModificationsMap(activeModifications),
    [activeModifications],
  );

  const activeModificationsCountByGroups = useMemo(
    () => getActiveModificationsCount({ activeModifications }),
    [activeModifications],
  );

  const maxModificationsByGroups = useMemo(
    () => getMaxModificationLimits({ modificationLimits }),
    [modificationLimits],
  );

  const dressingsDetails = useMemo(
    () => generateCombinedDressingDetails(activeModifications, mixedDressingDetails), // prettier-ignore
    [activeModifications, mixedDressingDetails],
  );

  const activeDressingsPortionsNumber = useMemo(
    () => getActiveDressingsPortionsNumbers(activeModifications),
    [activeModifications],
  );

  const outOfStockIngredients = useMemo(
    () => getOutOfStockModifications(initialModificationsUnsorted),
    [initialModificationsUnsorted],
  );

  const canMixDressings = useMemo(
    () => checkIfCanMixDressings({ restaurant, isDelivery, activeModifications }), // prettier-ignore
    [restaurant, isDelivery, activeModifications],
  );

  const hasReachedMaxModifiersCounts = useMemo(() => {
    if (!allModificationsMap) {
      return false;
    }

    return checkIfReachedMaxModifications({
      allModificationsMap,
      maxModificationsCount,
      modifiers: { additions, removals, substitutions },
    });
  }, [
    allModificationsMap,
    additions,
    removals,
    substitutions,
    maxModificationsCount,
  ]);

  const dressingModificationGroup = useMemo(() => {
    return modificationGroups?.find(
      (group) => group?.modificationIngredientKind === IngredientKind.Dressing,
    );
  }, [modificationGroups]);
  const dressingGroupName = dressingModificationGroup?.name;

  const hasNoSelectedDressing = !activeModifications.some(checkIfDressingIngredient); // prettier-ignore

  const maxDressingsPortionsNumber =
    (dressingGroupName
      ? modificationLimits[dressingGroupName]?.maxAggregateQuantity
      : DEFAULT_MAX_DRESSINGS_PORTIONS_NUMBER) ??
    DEFAULT_MAX_DRESSINGS_PORTIONS_NUMBER;

  const modifiersCount = additions.length + removals.length + substitutions.length; // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────────────

  return {
    isFetching,
    hasFailedToFetch,
    isCustomizationActive,
    isDetailsActive,
    isReviewActive,
    shouldShowNoDressingModal,
    shouldShowTooFewBasesModal,
    hasMadeModifications,
    canMixDressings,
    product,
    lineItemId,
    isDelivery,
    netPrice,
    calories,
    outOfStockIngredients,
    totalCost,
    quantity,
    restaurant,
    restaurantSlug,
    allModificationsMap,
    initialModificationsMap,
    activeModifications,
    activeModificationsMap,
    activeModificationsCountByGroups,
    modifications,
    modificationGroups,
    modificationLimits,
    modifiersCount,
    maxModificationsCount,
    maxModificationsByGroups,
    dressingsDetails,
    mixedDressingDetails,
    maxDressingsPortionsNumber,
    hasNoSelectedDressing,
    hasReachedMaxModifiersCounts,
    activeDressingsPortionsNumber,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_MAX_DRESSINGS_PORTIONS_NUMBER = 1;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = { actor: CustomizationMachineActorRef };

type ModificationsMap = CustomizationMachineTypes.ModificationsMap;
